import type { JournalAccount, Pagination } from "@/apis/gql/types";
import type { ServerFilters } from "@/hooks/useQueryFilters";
import { useTenant } from "@/providers/session";
import { gql, useLazyQuery, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { useCallback, useRef } from "react";

export const JOURNAL_ACCOUNTS_QUERY = gql`
  query GetAllJournalAccounts(
    $tenantId: String
    $page: Int
    $limit: Int
    $offset: Int
    $query: JSON
    $global: String
    $sort: String
    $order: String
  ) {
    journalAccounts(
      tenantId: $tenantId
      page: $page
      limit: $limit
      offset: $offset
      query: $query
      global: $global
      sort: $sort
      order: $order
    ) {
      data {
        journalAccountId
        tenantId
        parentAccountId
        entityId
        number
        name
        accountType
        entityType
        currency
        createdAt
        updatedAt
        deletedAt
        account {
          accountId
          accountName
          accountNo
          accountDescription
          accountType
          automatic
          parentAccountId
          taxCodeId
          min
          max
          buExport
          serviceDateExport
          faktor2
        }
        supplier {
          supplierId
          supplierName
          originalSupplierName
          parentSupplierId
          status
          imported
          createdAt
          exportedAt
          datevAccountNumber
          vatId
          taxCodeId
          isCategory
          accountId
        }
        bankAccount {
          bankAccountId
          name
          accountNumber
          type
          iban
        }
        entity {
          ... on Account {
            accountId
            accountName
            accountNo
            accountDescription
            accountType
            automatic
            parentAccountId
            taxCodeId
            min
            max
            buExport
            serviceDateExport
            faktor2
          }
          ... on Supplier {
            supplierId
            supplierName
            originalSupplierName
            parentSupplierId
            status
            imported
            createdAt
            exportedAt
            datevAccountNumber
            vatId
            taxCodeId
            isCategory
            accountId
          }
          ... on BankAccount {
            bankAccountId
            name
            accountNumber
            type
            iban
          }
        }
      }
      pagination {
        currentPage
        totalPages
        totalItems
        itemsPerPage
        hasNextPage
        hasPreviousPage
        nextPage
        previousPage
      }
    }
  }
`;

export const useGetAllJournalAccounts = ({
  query,
  page,
  limit,
  offset,
  global,
  sort,
  order,
}: {
  query: ServerFilters;
  page: number;
  limit: number;
  offset: number;
  global: string;
  sort: string;
  order: string;
}) => {
  const { tenantId } = useTenant();

  const [pagination, setPagination] = useState<Pagination>({
    currentPage: page,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: limit,
    hasNextPage: false,
    hasPreviousPage: false,
    nextPage: page,
    previousPage: page > 1 ? page - 1 : 1,
  });

  const { data, loading, error, refetch } = useQuery<{
    journalAccounts: {
      data: JournalAccount[];
      pagination: Pagination;
    };
  }>(JOURNAL_ACCOUNTS_QUERY, {
    variables: { tenantId, query, page, limit, offset, global, sort, order },
  });

  useEffect(() => {
    if (data?.journalAccounts?.pagination && !loading) {
      setPagination(data.journalAccounts.pagination);
    }
  }, [data?.journalAccounts?.pagination, loading]);

  return {
    data: data?.journalAccounts?.data,
    pagination,
    loading,
    error,
    refetch,
  };
};

export const useLazyGetJournalAccounts = ({
  limit = 10,
  page = 1,
  sort = "createdAt",
  order = "desc",
}: {
  limit?: number;
  page?: number;
  sort?: string;
  order?: string;
}) => {
  const { tenantId } = useTenant();

  const [getJournalAccounts, { data, loading, error }] = useLazyQuery<{
    journalAccounts: {
      data: JournalAccount[];
      pagination: Pagination;
    };
  }>(JOURNAL_ACCOUNTS_QUERY);

  const fetchJournalAccounts = async (
    params?: {
      query?: ServerFilters;
      global?: string;
    },
    pageOverride?: number,
  ) => {
    const currentPage = pageOverride ?? page;

    const result = await getJournalAccounts({
      variables: {
        tenantId,
        query: params?.query ?? undefined,
        global: params?.global ?? undefined,
        limit,
        page: currentPage,
        offset: (currentPage - 1) * limit,
        sort,
        order,
      },
    });

    return {
      data: result.data?.journalAccounts?.data ?? [],
      pagination: result.data?.journalAccounts?.pagination ?? null,
    };
  };

  return {
    fetchJournalAccounts,
    data: data?.journalAccounts?.data,
    loading,
    error,
  };
};
